import api from './api';

export interface DashboardHomeData {
  overview: {
    march2026TotalPipeline: number;
    march2026Breakdown?: string;
    fy26TotalPipeline: number;
    fy26Breakdown?: string;
    fy27TotalPipeline: number;
    fy27Breakdown?: string;
    activeMarch2026Deals: number;
    activeFY26Deals: number;
    activeFY27Deals: number;
    closingDeals: number;
    totalSalesReps: number;
  };
  march2026Breakdown: {
    high: number;
    medium: number;
    low: number;
    total: number;
  };
  fy26Breakdown: {
    high: number;
    medium: number;
    low: number;
    total: number;
  };
  fy27Breakdown: {
    high: number;
    medium: number;
    low: number;
    total: number;
  };
  config: {
    march2026Target: number;
    fy26Target: number;
    fy27Target: number;
    currentEBITDA: number;
    companyName: string;
  };
  teamSummary: Array<{
    id: string;
    name: string;
    department: string;
    pipelineValue: number;
    dealCount: number;
    status: string;
    target: number;
    march2026?: {
      total: number;
      dealCount: number;
    };
    fy26?: {
      total: number;
      dealCount: number;
    };
    fy27?: {
      total: number;
      dealCount: number;
    };
  }>;
}

export interface ExecutiveSummaryData {
  config: {
    companyName: string;
    currentEBITDA: number;
    march2026Target: number;
    fy26Target: number;
    fy27Target: number;
  };
  march2026: {
    pipeline: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    projection: number;
    gap: number;
    gapPercentage: number;
    onTrack: boolean;
  };
  fy26: {
    pipeline: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    costSavings: {
      total: number;
      initiativeCount: number;
    };
    projection: number;
    gap: number;
    gapPercentage: number;
    onTrack: boolean;
  };
  fy27: {
    pipeline: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    acquisitions: {
      total: number;
      count: number;
    };
    existingBase: {
      total: number;
      streamCount: number;
    };
    projection: number;
    gap: number;
    gapPercentage: number;
    onTrack: boolean;
  };
  breakdown: {
    byDepartment: Record<string, { count: number; value: number }>;
  };
  waterfall: {
    march2026: Array<{ label: string; value: number }>;
    fy26: Array<{ label: string; value: number }>;
    fy27: Array<{ label: string; value: number }>;
  };
}

export const dashboardService = {
  /**
   * Get dashboard home summary data
   */
  getDashboardHome: async (): Promise<DashboardHomeData> => {
    const response = await api.get<{ success: boolean; data: DashboardHomeData }>(
      '/dashboard/home'
    );
    return response.data.data;
  },

  /**
   * Get executive summary with EBITDA waterfall
   */
  getExecutiveSummary: async (): Promise<ExecutiveSummaryData> => {
    const response = await api.get<{ success: boolean; data: ExecutiveSummaryData }>(
      '/dashboard/executive-summary'
    );
    return response.data.data;
  },

  /**
   * Get team summary with detailed rep performance
   */
  getTeamSummary: async (): Promise<TeamSummaryData> => {
    const response = await api.get<{ success: boolean; data: TeamSummaryData }>(
      '/dashboard/team-summary'
    );
    return response.data.data;
  },

  /**
   * Get individual rep dashboard
   */
  getIndividualDashboard: async (repId: string): Promise<IndividualDashboardData> => {
    const response = await api.get<{ success: boolean; data: IndividualDashboardData }>(
      `/dashboard/individual/${repId}`
    );
    return response.data.data;
  },
};

export interface TeamSummaryData {
  config: {
    companyName: string;
    march2026Target: number;
    fy26Target: number;
    fy27Target: number;
    currentEBITDA: number;
  };
  teamTotals: {
    totalPipeline: number;
    totalTarget: number;
    totalDeals: number;
    highConfidencePipeline: number;
    mediumConfidencePipeline: number;
    lowConfidencePipeline: number;
    highConfidenceDeals: number;
    mediumConfidenceDeals: number;
    lowConfidenceDeals: number;
    teamAchievement: number;
    onTrack: boolean;
  };
  teamMetrics: Array<{
    id: string;
    repCode: string;
    fullName: string;
    email: string;
    department: string;
    role: string;
    target: number;
    status: string;
    pipeline: {
      total: number;
      high: number;
      medium: number;
      low: number;
    };
    march2026?: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    fy26?: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    fy27?: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    dealCounts: {
      total: number;
      high: number;
      medium: number;
      low: number;
    };
    top3Deals: Array<{
      id: string;
      dealCode: string;
      dealName: string;
      contractValue: number;
      weighting: number;
      weightedValue: number;
      confidenceLevel: string;
      status: string;
      targetCloseDate: string | null;
      commentary: string | null;
    }>;
    targetProgress: number;
    statusColor: 'success' | 'warning' | 'danger';
    territories: Array<{
      id: string;
      name: string;
      monthlyTarget: number;
      coveragePercentage: number;
    }>;
  }>;
}

export interface IndividualDashboardData {
  config: {
    companyName: string;
    fy26Target: number;
    currentEBITDA: number;
  };
  rep: {
    id: string;
    repCode: string;
    fullName: string;
    email: string;
    department: string;
    role: string;
    focusArea?: string;
    status: string;
  };
  performance: {
    target: number;
    pipeline: {
      total: number;
      high: number;
      medium: number;
      low: number;
    };
    march2026?: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    fy26?: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    fy27?: {
      total: number;
      high: number;
      medium: number;
      low: number;
      dealCount: number;
    };
    dealCounts: {
      total: number;
      high: number;
      medium: number;
      low: number;
    };
    targetProgress: number;
    targetGap: number;
    statusColor: 'success' | 'warning' | 'danger';
    onTrack: boolean;
  };
  top3Deals: Array<{
    id: string;
    dealCode: string;
    dealName: string;
    contractValue: number;
    weighting: number;
    weightedValue: number;
    confidenceLevel: string;
    status: string;
    targetCloseDate: string | null;
    category: string;
    commentary: string | null;
  }>;
  allDeals: Array<{
    id: string;
    dealCode: string;
    dealName: string;
    contractValue: number;
    weighting: number;
    weightedValue: number;
    confidenceLevel: string;
    status: string;
    targetCloseDate: string | null;
    revenueStartDate: string | null;
    category: string;
    department: string;
  }>;
  territories: Array<{
    id: string;
    territoryCode: string;
    territoryName: string;
    monthlyTarget: number;
    coveragePercentage: number;
    baselineRevenue: number;
  }>;
  breakdown: {
    byCategory: Record<string, { count: number; value: number }>;
  };
}
